/**
 * The public reads broadcast elements draw from (docs/features/badger-cohost.md, Endpoints): an athlete's profile (how
 * far along their race and in what place), a race's standings, and the roster's fuzzy search. Hidden athletes are in
 * none of them. Each reader is defensive: a row missing what it names is left out, and a body that isn't what it
 * should be reads as nothing, never an error on screen.
 */

import type { EventAthlete } from './eventAthletes'
import type { RaceGender } from './graphics'

export interface AthleteProgress {
  /** Kilometres covered along their race's course. */
  km: number
  /** Kilometres to the finish; null when the course's length isn't known. */
  toFinishKm: number | null
  /** When our tracker saw them finish, in epoch milliseconds (not the timing mat's), or null. */
  finishedAt: number | null
  /** Their newest fix is far from every part of the course: progress keeps its last value. */
  offCourse: boolean
}

export interface AthletePlace {
  /** Their place in the race, and how many are ranked in it. */
  overall: number
  of: number
  /** Their place among their gender, and how many; null without a gender. */
  gender: number | null
  ofGender: number | null
}

export interface AthleteProfile extends EventAthlete {
  /** Their newest fix, or null before their tracker has reported. */
  position: { latitude: number; longitude: number; recordedAt: number } | null
  /** The server says the newest fix is old (its STALE_AFTER). */
  stale: boolean
  progress: AthleteProgress | null
  place: AthletePlace | null
}

export interface StandingsRow {
  rank: number
  athleteId: string
  name: string
  bib: string | null
  progressKm: number
  /** Behind the first on the board: 0 for the first, null between finishers (a time we don't know). */
  behindKm: number | null
  /** Their newest fix, in epoch milliseconds. */
  recordedAt: number
  finishedAt: number | null
  stale: boolean
}

export interface Standings {
  /** When the server read them, in epoch milliseconds. */
  at: number
  staleAfterMs: number
  route: { id: string; name: string | null; distanceKm: number | null }
  gender: RaceGender | null
  rows: StandingsRow[]
}

/** A search's match, closest first, with how close (null without a score). */
export interface AthleteMatch extends EventAthlete {
  score: number | null
}

/** What a standings read asks for. */
export interface StandingsQuery {
  /** The race; null is the event's main course. */
  routeId: string | null
  gender?: RaceGender | null
  athleteIds?: readonly string[] | null
  limit?: number | null
}

const DEFAULT_STALE_AFTER_MS = 5 * 60_000

type Raw = Record<string, unknown>

const text = (value: unknown): string | null => (typeof value === 'string' && value.trim() ? value : null)
const number = (value: unknown): number | null => (typeof value === 'number' && Number.isFinite(value) ? value : null)
const time = (value: unknown): number | null => {
  const parsed = typeof value === 'string' ? Date.parse(value) : Number.NaN
  return Number.isFinite(parsed) ? parsed : null
}
const gender = (value: unknown): RaceGender | null => (value === 'female' || value === 'male' ? value : null)
const record = (value: unknown): Raw | null => (value && typeof value === 'object' && !Array.isArray(value) ? (value as Raw) : null)
const dataOf = (body: unknown): unknown => record(body)?.data

/** A roster row as every athlete endpoint sends it; null without an id and a name. */
function athleteRow(raw: unknown): EventAthlete | null {
  const row = record(raw)
  const id = text(row?.id)
  const name = text(row?.name)
  if (!row || !id || !name) return null
  return { id, name, bib: text(row.bib), gender: gender(row.gender), routeId: text(row.route_id), photoUrl: text(row.photo_url) }
}

/** `GET …/athletes/:id`'s profile, or null when the body doesn't name an athlete. */
export function parseAthleteProfile(body: unknown): AthleteProfile | null {
  const data = record(dataOf(body))
  const athlete = athleteRow(data)
  if (!data || !athlete) return null
  const position = record(data.position)
  const latitude = number(position?.latitude)
  const longitude = number(position?.longitude)
  const recordedAt = time(position?.recorded_at)
  const progress = record(data.progress)
  const km = number(progress?.km)
  const place = record(data.place)
  const overall = number(place?.overall)
  const of = number(place?.of)
  return {
    ...athlete,
    position:
      latitude !== null && longitude !== null && recordedAt !== null && Math.abs(latitude) <= 90 && Math.abs(longitude) <= 180
        ? { latitude, longitude, recordedAt }
        : null,
    stale: data.stale === true,
    progress:
      progress && km !== null
        ? { km, toFinishKm: number(progress.to_finish_km), finishedAt: time(progress.finished_at), offCourse: progress.off_course === true }
        : null,
    place: place && overall !== null && of !== null ? { overall, of, gender: number(place.gender), ofGender: number(place.of_gender) } : null,
  }
}

/** `GET …/standings`, or null when the body has no race. Rows missing an athlete, a rank or progress are left out. */
export function parseStandings(body: unknown): Standings | null {
  const data = record(dataOf(body))
  const route = record(data?.route)
  const routeId = text(route?.id)
  if (!data || !route || !routeId) return null
  const staleAfter = number(data.stale_after_s)
  const rows = (Array.isArray(data.rows) ? data.rows : []).flatMap((raw): StandingsRow[] => {
    const row = record(raw)
    const athleteId = text(row?.athlete_id)
    const rank = number(row?.rank)
    const progressKm = number(row?.progress_km)
    if (!row || !athleteId || rank === null || progressKm === null) return []
    return [
      {
        rank,
        athleteId,
        name: text(row.name) ?? '',
        bib: text(row.bib),
        progressKm,
        behindKm: number(row.behind_km),
        recordedAt: time(row.recorded_at) ?? Number.NaN,
        finishedAt: time(row.finished_at),
        stale: row.stale === true,
      },
    ]
  })
  return {
    at: time(data.at) ?? Date.now(),
    staleAfterMs: staleAfter !== null && staleAfter > 0 ? staleAfter * 1000 : DEFAULT_STALE_AFTER_MS,
    route: { id: routeId, name: text(route.name), distanceKm: number(route.distance_km) },
    gender: gender(data.gender),
    rows,
  }
}

/** `GET …/athletes?q=`'s matches, closest first. */
export function parseAthleteMatches(body: unknown): AthleteMatch[] {
  const rows = dataOf(body)
  if (!Array.isArray(rows)) return []
  return rows.flatMap((raw) => {
    const athlete = athleteRow(raw)
    return athlete ? [{ ...athlete, score: number(record(raw)?.score) }] : []
  })
}

/** The standings query as the endpoint's query string (without the `?`), in a stable order so equal boards share a cache. */
export function standingsSearch(query: StandingsQuery): string {
  const params = new URLSearchParams()
  if (query.routeId) params.set('route_id', query.routeId)
  if (query.gender) params.set('gender', query.gender)
  if (query.athleteIds?.length) params.set('athlete_ids', [...query.athleteIds].sort().join(','))
  if (query.limit) params.set('limit', String(query.limit))
  return params.toString()
}
